import type { ReactNode } from 'react';

import type {
  NavbarV2SerializableNode,
  TextLeafProps,
  TNavbarDropdownElement,
  TNavbarDropdownElementColumn,
  TNavbarDropdownElementItem,
  TNavbarItemElement,
  TNavbarMenuBurgerContentElement,
  TNavbarMenuBurgerElement,
  TNavbarMenuElement,
  TNavbarMenuListElement,
  TNavbarMenuListGroupElement,
} from '@shared/dream-components';

import Text from '../nodes/Text';
import NavbarMenu from './nodes/NavbarMenu';
import NavbarMenuBurger from './nodes/NavbarMenuBurger';
import NavbarMenuBurgerContent from './nodes/NavbarMenuBurgerContent';
import NavbarMenuDropdown from './nodes/NavbarMenuDropdown';
import NavbarMenuDropdownColumn from './nodes/NavbarMenuDropdownColumn';
import NavbarMenuDropdownItem from './nodes/NavbarMenuDropdownItem';
import NavbarMenuItem from './nodes/NavbarMenuItem';
import NavbarMenuList from './nodes/NavbarMenuList';
import NavbarMenuListGroup from './nodes/NavbarMenuListGroup';

interface Props {
  node: NavbarV2SerializableNode;
  parent?: NavbarV2SerializableNode;
  children: ReactNode | ReactNode[];
  screen: 'desktop' | 'mobile';
}

const NavbarRendererV2 = ({ node, children, screen, parent }: Props) => {
  switch (node.type) {
    case 'navbar_menu':
      return (
        <NavbarMenu node={node as TNavbarMenuElement} screen={screen}>
          {children}
        </NavbarMenu>
      );
    case 'navbar_menu_list':
      return (
        <NavbarMenuList node={node as TNavbarMenuListElement} screen={screen}>
          {children}
        </NavbarMenuList>
      );
    case 'navbar_menu_list_group':
      return (
        <NavbarMenuListGroup node={node as TNavbarMenuListGroupElement} screen={screen} parent={parent}>
          {children}
        </NavbarMenuListGroup>
      );
    case 'navbar_item':
      return (
        <NavbarMenuItem node={node as TNavbarItemElement} screen={screen}>
          {children}
        </NavbarMenuItem>
      );
    case 'navbar_dropdown':
      return (
        <NavbarMenuDropdown node={node as TNavbarDropdownElement} screen={screen}>
          {children}
        </NavbarMenuDropdown>
      );
    case 'navbar_dropdown_column':
      return (
        <NavbarMenuDropdownColumn node={node as TNavbarDropdownElementColumn} screen={screen}>
          {children}
        </NavbarMenuDropdownColumn>
      );
    case 'navbar_dropdown_item':
      return (
        <NavbarMenuDropdownItem node={node as TNavbarDropdownElementItem} screen={screen}>
          {children}
        </NavbarMenuDropdownItem>
      );
    case 'navbar_menu_burger':
      return (
        <NavbarMenuBurger node={node as TNavbarMenuBurgerElement} screen={screen}>
          {children}
        </NavbarMenuBurger>
      );
    case 'navbar_menu_burger_content':
      return (
        <NavbarMenuBurgerContent node={node as TNavbarMenuBurgerContentElement} screen={screen}>
          {children}
        </NavbarMenuBurgerContent>
      );
    case 'navbar_item_text_container':
    case 'navbar_dropdown_item_title':
    case 'navbar_dropdown_item_description':
      return children;
    case 'text':
      return <Text node={node as TextLeafProps['leaf']}>{children}</Text>;
    default:
      return null;
  }
};

export default NavbarRendererV2;
